/* =========================================================================
   The mode switcher — "Trivia ⌄" on the home lockup.

   Eric 2026-08-21, from the game-HUD reference: the title should carry a
   dropdown that jumps straight to the arcade games instead of making players
   scroll to the tiles at the bottom of home. It sits on the hero lockup where
   the static "Trivia" subtitle used to be — the bankbar is hidden on home
   (auth.ts:showHome), so the lockup IS the top of the home screen.

   DEPENDENCY-INJECTED on purpose. This module needs playArcade() and
   openFriendlyPlay(), but arcade.ts / friendlyController.ts are big consumers
   that would form an import cycle the moment either one wanted to refresh the
   pill. Taking them as callbacks at boot keeps this a leaf: it imports nothing
   from the app, so it can never sit in a cycle, and the whole thing is
   testable with four stubs.

   Availability is never assumed. The arcade section only exists once earned
   (isArcadeUnlocked) and Friendly Play is behind a flag, so the menu is built
   fresh on every open and the chevron disappears entirely when there is
   nowhere else to go — a dropdown that opens onto one disabled row is worse
   than no dropdown.
   ========================================================================= */
import { icon } from './icons';

/** The four mini-games, mirroring arcade.ts's ArcadeGame union. */
export type ArcadeGameKey = 'match3' | 'maryMatch3' | 'manna' | 'wheel';

/** What the switcher needs from the app, injected at boot to avoid cycles. */
export interface ModeSwitcherDeps {
  isArcadeUnlocked: () => boolean;
  isFriendlyEnabled: () => boolean;
  playArcade: (game: ArcadeGameKey) => void;
  openFriendlyPlay: () => void;
}

interface ModeEntry {
  key: string;
  /** Brand icon markup (ui/icons.ts) — rendered raw, never escaped. */
  icon: string;
  label: string;
  run: () => void;
}

const PILL_ID = 'modePill';
const MENU_ID = 'modeMenu';
const CURRENT_LABEL = 'Trivia';

let deps: ModeSwitcherDeps | null = null;
/** Document listeners are global and must be attached exactly once. */
let docWired = false;

/** The lockup's mode control. Replaces the static "Trivia" subtitle. */
export const modePillHTML = /* html */ `
<div class="mode-wrap">
  <button class="mode-pill" id="${PILL_ID}" type="button" aria-haspopup="menu" aria-expanded="false" aria-controls="${MENU_ID}">
    <span class="mode-pill-label">${CURRENT_LABEL}</span>
    <svg class="mode-chev" viewBox="0 0 16 16" aria-hidden="true" focusable="false" fill="currentColor"><path d="M3.6 5.8a1.1 1.1 0 0 1 1.6 0L8 8.6l2.8-2.8a1.1 1.1 0 1 1 1.6 1.6l-3.6 3.6a1.1 1.1 0 0 1-1.6 0L3.6 7.4a1.1 1.1 0 0 1 0-1.6Z"/></svg>
  </button>
  <div class="mode-menu" id="${MENU_ID}" role="menu" aria-label="Game mode" hidden></div>
</div>`;

/**
 * What the player can reach right now. Trivia is always first and always the
 * current mode; the rest appear only when their gate is open. PURE given deps.
 */
export function availableModes(d: ModeSwitcherDeps): ModeEntry[] {
  const out: ModeEntry[] = [];
  if (d.isFriendlyEnabled()) {
    out.push({ key: 'friendly', icon: icon('book', 18), label: 'Friendly Play', run: () => d.openFriendlyPlay() });
  }
  if (d.isArcadeUnlocked()) {
    out.push(
      { key: 'match3', icon: icon('cross', 18), label: 'Faith Match', run: () => d.playArcade('match3') },
      { key: 'maryMatch3', icon: icon('star', 18), label: 'Mother Mary Match-3', run: () => d.playArcade('maryMatch3') },
      { key: 'manna', icon: icon('gift', 18), label: 'Manna Drop', run: () => d.playArcade('manna') },
      { key: 'wheel', icon: icon('compass', 18), label: 'Bonus Wheel', run: () => d.playArcade('wheel') },
    );
  }
  return out;
}

function pill(): HTMLButtonElement | null {
  return document.getElementById(PILL_ID) as HTMLButtonElement | null;
}
function menu(): HTMLElement | null {
  return document.getElementById(MENU_ID);
}

function items(): HTMLButtonElement[] {
  const m = menu();
  return m ? Array.from(m.querySelectorAll<HTMLButtonElement>('.mode-item')) : [];
}

export function isModeMenuOpen(): boolean {
  const p = pill();
  return !!p && p.getAttribute('aria-expanded') === 'true';
}

export function closeModeMenu(focusPill = false): void {
  const p = pill();
  const m = menu();
  if (!p || !m) return;
  p.setAttribute('aria-expanded', 'false');
  m.hidden = true;
  m.classList.remove('open');
  if (focusPill) p.focus();
}

function openModeMenu(): void {
  const p = pill();
  const m = menu();
  if (!p || !m || !deps) return;

  const modes = availableModes(deps);
  if (!modes.length) return; // nothing to switch to — refreshModeSwitcher hid the chevron

  m.innerHTML =
    `<div class="mode-item is-current" role="menuitem" aria-current="true" tabindex="-1">` +
    `<span class="mode-item-ic" aria-hidden="true">${icon('cross', 16)}</span>` +
    `<span class="mode-item-label">${CURRENT_LABEL}</span>` +
    `<span class="mode-item-tick" aria-hidden="true">✓</span>` +
    `</div>` +
    modes
      .map(
        (e) =>
          `<button type="button" class="mode-item" role="menuitem" tabindex="-1" data-key="${e.key}">` +
          `<span class="mode-item-ic" aria-hidden="true">${e.icon}</span>` +
          `<span class="mode-item-label">${e.label}</span>` +
          `</button>`,
      )
      .join('');

  m.hidden = false;
  // Next frame so the transition has a from-state to animate out of.
  requestAnimationFrame(() => m.classList.add('open'));
  p.setAttribute('aria-expanded', 'true');

  const first = items()[0];
  if (first) first.focus();
}

function toggleModeMenu(): void {
  if (isModeMenuOpen()) closeModeMenu(true);
  else openModeMenu();
}

function moveFocus(delta: number): void {
  const list = items();
  if (!list.length) return;
  const at = list.indexOf(document.activeElement as HTMLButtonElement);
  const next = (((at < 0 ? 0 : at + delta) % list.length) + list.length) % list.length;
  list[next].focus();
}

/**
 * Show or hide the chevron for what's actually reachable. Cheap and
 * idempotent — call it whenever home is shown; an arcade unlock that landed
 * mid-session shows up on the next visit without a reload.
 */
export function refreshModeSwitcher(): void {
  const p = pill();
  if (!p || !deps) return;
  const any = availableModes(deps).length > 0;
  p.classList.toggle('inert', !any);
  p.disabled = !any;
  if (!any) closeModeMenu();
}

/** Wire the switcher once at boot. */
export function initModeSwitcher(d: ModeSwitcherDeps): void {
  deps = d;
  const p = pill();
  const m = menu();
  if (!p || !m) return;

  // Per-NODE guard. The pill/menu handlers belong to these elements, so a
  // re-wire on the same markup must not stack a second copy.
  if (p.dataset.wired !== '1') {
    p.dataset.wired = '1';
    p.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleModeMenu();
    });
  }
  if (m.dataset.wired !== '1') {
    m.dataset.wired = '1';
    m.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest('.mode-item') as HTMLButtonElement | null;
      if (!btn || !deps) return;
      const key = btn.dataset.key;
      closeModeMenu();
      if (!key) return; // the current-mode row: already here, just close
      const entry = availableModes(deps).find((x) => x.key === key);
      if (entry) entry.run();
    });
  }

  // Document-level handlers are attached ONCE for the lifetime of the module,
  // never per call. They also re-resolve the pill and menu by id at event time
  // rather than closing over the nodes: a stale closure over a replaced menu
  // would see "the click was outside me" and shut the live one.
  if (!docWired) {
    docWired = true;

    // Escape closes from anywhere; arrows walk the list.
    document.addEventListener('keydown', (e) => {
      if (!isModeMenuOpen()) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeModeMenu(true);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveFocus(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveFocus(-1);
      }
    });

    // Any tap outside dismisses — the menu is a popover, not a mode.
    document.addEventListener('click', (e) => {
      if (!isModeMenuOpen()) return;
      const liveMenu = menu();
      const livePill = pill();
      const t = e.target as Node;
      if (liveMenu && !liveMenu.contains(t) && t !== livePill) closeModeMenu();
    });
  }

  refreshModeSwitcher();
}
